import { useGetResponseTimes } from '@/apis/agency/systemSentiment/hook';
import Pagination, { usePagination } from '@/components/Common/Pagination';
import { Box, LinearProgress, Stack, Typography, Skeleton } from '@mui/material';

export default function ResponseTime({ filters }: { filters: Record<string, any> }) {
  const { currentPage: page, perPage, setPage, setPerPage } = usePagination();

  const { data: responseTimes, isLoading } = useGetResponseTimes({
    page,
    per_page: perPage,
    filter: filters
  });

  return (
    <Box
      p={3}
      border='1px solid #ECECEC'
      borderRadius={2}
      bgcolor='#fff'
      width='100%'
      display='flex'
      flexDirection='column'
      minHeight={420}
    >
      {isLoading ? (
        <Skeleton width={220} height={28} sx={{ mb: 3 }} />
      ) : (
        <Typography mb={3} fontSize={20} fontWeight={500}>
          Response Time by Tool
        </Typography>
      )}

      <Stack spacing={2} flex={1}>
        {(isLoading ? Array.from({ length: 7 }) : responseTimes?.data || []).map((item: any, index: number) => {
          if (isLoading) {
            return (
              <Box key={index}>
                <Stack direction='row' justifyContent='space-between' mb={1}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Skeleton width={120} height={20} />
                    <Skeleton width={60} height={20} sx={{ borderRadius: '24px' }} />
                  </Stack>

                  <Stack direction='row' spacing={1}>
                    <Skeleton width={50} height={20} />
                    <Skeleton width={80} height={18} />
                  </Stack>
                </Stack>

                <Skeleton variant='rectangular' height={6} sx={{ borderRadius: 2 }} />
              </Box>
            );
          }

          const isSlow = item.avg_response_time_s > 3;
          const percentage = Math.min((item.avg_response_time_s / 3.5) * 100, 100);

          return (
            <Box key={item.tool}>
              <Stack direction='row' justifyContent='space-between' mb={1.5}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography fontWeight={500}>{item.tool}</Typography>

                  <Box px={1} py={0.2} borderRadius='24px' bgcolor='#F3F4F6' fontSize={12}>
                    {item.executor_label}
                  </Box>
                </Stack>

                <Stack direction='row' spacing={1}>
                  <Typography color={isSlow ? '#DC2626' : '#16A34A'}>{item.avg_response_time_s.toFixed(2)}s</Typography>
                  <Typography fontSize={12}>p95: {item.p95_response_time_s.toFixed(2)}s</Typography>
                </Stack>
              </Stack>

              <LinearProgress
                variant='determinate'
                value={percentage}
                color={isSlow ? 'error' : 'success'}
                sx={{ height: 6, borderRadius: 2, mb: 1 }}
              />
            </Box>
          );
        })}
      </Stack>
      <Box mt={2} display='flex' justifyContent='end'>
        {isLoading ? (
          <Skeleton width={200} height={32} />
        ) : (
          <Pagination
            totalPages={responseTimes?.meta?.total_pages || 1}
            totalCount={responseTimes?.meta?.total_count ?? 0}
            currentPage={page}
            perPage={perPage}
            onChange={setPage}
            onPerPageChange={setPerPage}
          />
        )}
      </Box>
    </Box>
  );
}
